import { Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { useGetCampaignSummaryQuery } from "../../api";
import { ProductTitleBlock } from "../../components/products/ProductTitleBlock";
import { NavigationBreadcrumbs, ReportsTabs } from "../../components/ui";
import { CustomLoader } from "../../components/ui/CustomLoader";
import { AppRoutes } from "../../types/enums";
import { lazy, ReactNode } from "react";
import { AccountsWrapper } from "./AccountsWrapper";
import { useAppSelector } from "../../hooks";

const AccountsListPage = lazy(() => import("../../pages/AccountsListPage"));
const AccountsPenetrationPage = lazy(
  () => import("../../pages/AccountsPenetrationPage"),
);
const AccountsEngagementPage = lazy(
  () => import("../../pages/AccountsEngagementPage"),
);
const AccountsReportsPage = lazy(
  () => import("../../pages/AccountsReportsPage"),
);
const RecommendedPage = lazy(() => import("../../pages/RecommendedPage"));

const mapper: Record<string, ReactNode> = {
  list: <AccountsWrapper children={<AccountsListPage />} />,
  rate: <AccountsWrapper children={<AccountsPenetrationPage />} />,
  score: <AccountsWrapper children={<AccountsEngagementPage />} />,
  reports: <AccountsReportsPage />,
  recommended: <RecommendedPage />,
};

export const AccountsLayout = () => {
  const { campaign_id, tab } = useParams();
  const { isEditor } = useAppSelector((state) => state.company.userRoles);

  const { data, isLoading } = useGetCampaignSummaryQuery({
    campaign_id: campaign_id!,
  });

  const breadcrumbs = [
    {
      title: data?.product_name ?? "company name",
      path: `${AppRoutes.CAMPAIGNS}/${campaign_id}`,
    },
    {
      title: "Accounts",
    },
  ];

  if (isLoading || !data) {
    return <CustomLoader />;
  }

  return (
    <Stack sx={{ px: { sm: 8, md: 8, lg: 10, xl: 12 }, pt: 4, pb: 6 }} gap={3}>
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
      <Stack gap={"8px"}>
        <ProductTitleBlock
          product_name={data.product_name}
          company_name={data.company_name}
        />
        <ReportsTabs
          isNeedUpdate={data.has_reports_needing_update}
          isDisabled={
            data.total_accounts_analyzed === 0 ||
            (!data.one_account_plan_complete && !isEditor)
          }
          isNoAccounts={data.total_accounts === 0}
          campaignPurpose={data.campaign_purpose}
        />
      </Stack>

      {mapper[tab!]}
    </Stack>
  );
};
